import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ENGAGEMENT_LABELS } from "../engagement-metrics/engagement.labels";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25Util } from "../../../util/s25-util";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { IdCardConfigComponent } from "./id.card.config.component";
import { IdCardsStore } from "./id.cards.store";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../util/s25.resource";
import { CardConfig, CardService } from "../../../services/card.service";

@TypeManagerDecorator("s25-ng-id-cards-config")
@Component({
    standalone: true,
    selector: "s25-ng-id-cards-config",
    template: `
        <h2>{{ labels.idCardsTitle }}</h2>
        @if (selectedConfig.resource.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (selectedConfig.value()) {
            <s25-ng-id-card-config [config]="selectedConfig.value()" (done)="selectedConfigId.set(undefined)" />
        } @else if (store.configsResource.resource.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (store.configsResource.resource.error()) {
            Something went wrong loading card configs
        } @else {
            <div class="configs">
                @for (config of store.configs(); track config.id) {
                    <s25-ng-button
                        [type]="'none'"
                        class="config"
                        (clicked)="selectedConfigId.set(config.id!)"
                        [ariaLabel]="'Edit config'"
                    >
                        <div class="config--inner">
                            <div class="stripe">
                                <span class="name">{{ config.name }}</span>
                            </div>
                            <div class="content">{{ config.description }}</div>
                        </div>
                    </s25-ng-button>
                }
                <s25-ng-button [type]="'none'" class="add config" (clicked)="newConfig()" [ariaLabel]="'Add config'">
                    <div class="config--inner">
                        <div class="stripe">
                            <span class="name">Add Config</span>
                        </div>
                        <div class="content">
                            <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            <p>Click here to add a new config</p>
                        </div>
                    </div>
                </s25-ng-button>
            </div>
        }
    `,
    styles: `
        .configs {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
        }

        .config {
            background-color: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            aspect-ratio: 27/17;
            grid-column: span 10;
            box-shadow:
                1px 1px 0 #dadada,
                3px 3px 9px #0000008c;
            border-radius: 1rem;
            cursor: pointer;
            transition: scale 100ms;

            &:hover {
                scale: 1.005;
            }
        }

        .configs > * {
            height: 15rem;
        }

        :host ::ng-deep .config button {
            border: none;
        }

        .config--inner {
            height: 100%;
            padding-top: 4%;
            display: flex;
            flex-direction: column;
        }

        .config .stripe {
            height: 24%;
            background: #474747;
            color: white;
            align-content: center;
            text-align: center;
            container-type: size;
        }

        .config .name {
            font-size: 33cqb;
        }

        .config .content {
            padding: 1rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-grow: 1;
            align-items: center;
        }

        .add {
            opacity: 0.5;
        }

        .add s25-ng-icon {
            font-size: 3em;
            color: var(--cn-blue);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineStaticComponent, S25ButtonComponent, IdCardConfigComponent, S25IconComponent],
    providers: [IdCardsStore],
})
export class IdCardsConfigComponent {
    readonly labels = ENGAGEMENT_LABELS;
    readonly s25Util = S25Util;
    readonly store = inject(IdCardsStore);
    readonly service = inject(CardService);

    readonly selectedConfigId = signal<number | "new" | undefined>(undefined);
    readonly selectedConfig = s25Resource({
        params: () => this.selectedConfigId(),
        loader: async ({ params: configId, abortSignal }) => {
            if (configId === null) return undefined;
            const isNew = configId === "new";
            if (isNew) return <CardConfig>{ name: "New Card Config", description: "", tracks: [] };
            return await this.service.getConfig(configId);
        },
    });

    newConfig() {
        this.selectedConfigId.set("new");
    }
}
